import React, { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';

import { useNotifications } from '../hooks/useNotifications';
import {
  AdminMfaStatus,
  getAdminMfaStatus,
  getUserProfile,
  updateUserProfile,
} from '../services/apiService';
import cognitoAuthService, {
  TotpChallenge,
  TotpChallengeRequiredError,
} from '../services/cognitoAuthService';
import { User, UserUpdate } from '../types';
import { isAdmin } from '../utils/auth';
import { discardPendingSignInAction } from '../utils/pendingSignInAction';
import { signalSessionExpired, subscribeToSessionExpiry } from '../utils/sessionExpiry';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User | null>;
  /** Finishes a log-in answered with an authenticator challenge, exactly as `login` would. */
  completeTotpSignIn: (challenge: TotpChallenge, code: string) => Promise<User | null>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
  setUserData: (userData: User) => void;
  isAuthenticated: boolean;
  /** Two-factor status of a signed-in admin; null for everyone else and when the fetch failed. */
  adminMfa: AdminMfaStatus | null;
  retryProfileFetch: () => Promise<void>;
  /** Rises by one each time the session ends; the pop-up host opens on a rise. */
  sessionExpiredCount: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Two-factor status for an admin; null for anyone else and when the fetch fails. A failure is a
 * UX guard only (the page redirect), so it is logged and never blocks the session.
 */
const fetchAdminMfa = async (profile: User): Promise<AdminMfaStatus | null> => {
  if (!isAdmin(profile)) return null;
  try {
    return await getAdminMfaStatus();
  } catch (error: unknown) {
    console.error('Failed to fetch admin two-factor status:', error);
    return null;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [adminMfa, setAdminMfa] = useState<AdminMfaStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpiredCount, setSessionExpiredCount] = useState(0);
  const { addNotification } = useNotifications();
  // Whether this tab holds a session, readable from the mount-only storage listener.
  const hasSessionRef = useRef(false);

  useEffect(() => {
    hasSessionRef.current = !!token;
  }, [token]);

  // Check for existing Cognito token on mount
  useEffect(() => {
    // Shared by the three expiry sites below (mount-time 401/403 profile fetch, mount-time
    // refresh failure, cross-tab storage listener): clear the tokens, then signal. The
    // subscription below ends the session in place; the pop-up host opens the sign-in.
    const expireSession = () => {
      cognitoAuthService.logout();
      signalSessionExpired();
    };

    // Subscribed before checkAuth so a signal latched before mount is delivered first.
    const unsubscribeFromExpiry = subscribeToSessionExpiry(() => {
      setUser(null);
      setToken(null);
      setAdminMfa(null);
      setSessionExpiredCount((count) => count + 1);
    });

    const checkAuth = async () => {
      try {
        const cognitoToken = await cognitoAuthService.getValidAccessToken();
        if (cognitoToken) {
          setToken(cognitoToken);

          // Try to fetch user profile
          try {
            const userData: User = await getUserProfile();
            setUser(userData);
            setAdminMfa(await fetchAdminMfa(userData));
          } catch (profileError) {
            console.error('Failed to fetch user profile:', profileError);

            // Check if it's an auth error vs network error
            const error = profileError as any;
            if (error.response?.status === 401 || error.response?.status === 403) {
              expireSession();
            } else {
              // Network error or other issue - keep user authenticated but log the error
              console.error('Network error fetching profile, user remains authenticated');
            }
          }
        } else {
          // No valid token, ensure we're in logged out state
          setUser(null);
          setToken(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        const authError = error as any;

        // Check if it's a token refresh failure
        if (authError?.message?.includes('invalid') || authError?.message?.includes('expired')) {
          expireSession();
        } else {
          // Other error - clear state but don't redirect
          setUser(null);
          setToken(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Set up storage listener for cross-tab logout synchronization
    const handleStorageChange = () => {
      // Check if tokens were cleared in another tab
      const token = cognitoAuthService.getAccessToken();
      if (!token && hasSessionRef.current) {
        expireSession();
      }
    };

    // Listen for storage changes (logout in other tabs)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      unsubscribeFromExpiry();
    };
  }, []);

  /**
   * Publishes the freshly stored token, then loads the profile with one retry.
   * A second failure keeps the session (token only) and tells the user the profile is pending.
   */
  const loadProfileAfterSignIn = async (): Promise<User | null> => {
    const accessToken = cognitoAuthService.getAccessToken();
    setToken(accessToken);

    // Small delay to ensure token is properly set before API calls
    await new Promise((resolve) => setTimeout(resolve, 100));

    let userData: User | null = null;
    let profileFetchAttempts = 0;
    const maxAttempts = 2;

    while (profileFetchAttempts < maxAttempts && !userData) {
      try {
        profileFetchAttempts++;

        // Increased timeout to match API service timeout (15s)
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Profile fetch timeout after 15 seconds')), 15000),
        );

        userData = (await Promise.race([getUserProfile(), timeoutPromise])) as User;

        setUser(userData);
        break;
      } catch {
        // If this is the last attempt, proceed with partial authentication
        if (profileFetchAttempts >= maxAttempts) {
          addNotification('Login successful! Profile data will be loaded shortly.', 'info');
          // Keep the user logged in with just the token - they can retry profile fetch later
          // Don't set user data but keep the token so they're considered authenticated
          break;
        }

        // Wait before retrying with exponential backoff
        const delayMs = 1000 * profileFetchAttempts; // 1s, 2s
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    if (userData) setAdminMfa(await fetchAdminMfa(userData));
    return userData;
  };

  /** Runs one sign-in call, then loads the profile; any failure clears the partial session and rethrows. */
  const signInThrough = async (authenticate: () => Promise<unknown>): Promise<User | null> => {
    discardPendingSignInAction();
    try {
      setIsLoading(true);
      await authenticate();
      return await loadProfileAfterSignIn();
    } catch (error: unknown) {
      // A challenge is the expected first answer for an authenticator account, and it carries the live session.
      if (error instanceof TotpChallengeRequiredError) throw error;
      console.error('Login failed:', error);
      // Clear any partial auth state on Cognito login failure
      cognitoAuthService.logout();
      setUser(null);
      setToken(null);
      setAdminMfa(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const login = (email: string, password: string): Promise<User | null> =>
    signInThrough(() => cognitoAuthService.login({ email, password }));

  const completeTotpSignIn = (challenge: TotpChallenge, code: string): Promise<User | null> =>
    signInThrough(() => cognitoAuthService.completeTotpChallenge({ ...challenge, code }));

  const logout = () => {
    discardPendingSignInAction();
    cognitoAuthService.logout();
    setToken(null);
    setUser(null);
    setAdminMfa(null);
  };

  const updateUser = async (userData: Partial<User>): Promise<void> => {
    try {
      setIsLoading(true);

      if (user) {
        // Only include fields that have actual values to avoid overwriting with undefined
        const updateData: UserUpdate = {};
        if (userData.full_name !== undefined) updateData.full_name = userData.full_name;
        if (userData.email !== undefined) updateData.email = userData.email;
        if (userData.phone_number !== undefined) updateData.phone_number = userData.phone_number;
        if (userData.profile_picture_url !== undefined)
          updateData.profile_picture_url = userData.profile_picture_url;

        // Call backend API to persist changes
        const updatedUser = await updateUserProfile(updateData);

        // Update local state with the response from backend
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const setUserData = (userData: User) => {
    // Update local state only, without making API calls
    setUser(userData);
  };

  const retryProfileFetch = async (): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      setIsLoading(true);

      const userData: User = await getUserProfile();
      setUser(userData);
    } catch (error: any) {
      console.error('Profile fetch retry failed:', error);
      addNotification('Unable to load profile data. Please try again later.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    completeTotpSignIn,
    logout,
    updateUser,
    setUserData,
    isAuthenticated: cognitoAuthService.isAuthenticated() && !!token,
    adminMfa,
    retryProfileFetch,
    sessionExpiredCount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
